import React, { useMemo } from 'react';
import { Table, Input, Switch, Button, Space, Typography, Tag, Tooltip } from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  PlusOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useLanguage } from '../contexts/LanguageContext';

const { Text } = Typography;

/**
 * 共用的「表單字段顯示設定」表格。
 *
 * 給 EFormFieldDisplaySetting / MetaFlowFieldDisplaySetting 共用。
 * 每一列代表一個 fieldId（或同一個 fieldId 下的某個 JSON Key 子列）。
 *
 * 資料模型（與 backend FieldDisplaySetting 對齊）：
 *   { rowKey, fieldId, fieldType, inputType, originalInputType?,
 *     originalLabel, displayLabel, showInList, order,
 *     jsonKey?, screenId?, screenIndex? }
 *
 * 排序：
 *   - 用「↑ / ↓」按鈕；按下時相鄰兩列的 order 互換，整個陣列依 order 重排。
 *   - JSON 子列與一般列共享同一全域 order，使用者可混合排序。
 *
 * JSON 子列：
 *   - 將任一列轉為 JSON 模式時，inputType 變 'json'，rowKey = `${fieldId}::${jsonKey}`。
 *   - 同一個 fieldId 可有多個 JSON 子列，每個指向不同 jsonKey。
 *   - 第一列若 inputType=='json' 為「主 JSON 列」，後續加入的子列 rowKey 用同樣規則。
 *   - 移除 JSON 子列只清這一列，其他 fieldId 子列保留。
 */
const FieldDisplaySettingTable = ({
  value = [],
  onChange,
  showScreenColumn = false,
  supportJson = true
}) => {
  const { t } = useLanguage();

  // 依 order 排序的 view（不直接 mutate value）
  const sortedRows = useMemo(() => {
    return value
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [value]);

  // 將排序後重新 normalize order 連續整數，避免長期累積大跳號
  const commit = (next) => {
    const normalized = next.map((r, i) => ({ ...r, order: i }));
    onChange?.(normalized);
  };

  const handleFieldChange = (rowKey, key, val) => {
    const next = sortedRows.map((r) =>
      r.rowKey === rowKey ? { ...r, [key]: val } : r
    );
    commit(next);
  };

  const handleMove = (rowKey, direction) => {
    const idx = sortedRows.findIndex((r) => r.rowKey === rowKey);
    if (idx < 0) return;
    const swap = direction === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= sortedRows.length) return;
    const next = sortedRows.slice();
    [next[idx], next[swap]] = [next[swap], next[idx]];
    commit(next);
  };

  // 將一列轉為 JSON 模式 / 為現有 JSON 列再新增一個 JSON Key 子列
  const handleAddJsonRow = (sourceRow) => {
    const isAlreadyJson = (sourceRow.inputType || '') === 'json' || !!sourceRow.jsonKey;
    const next = sortedRows.slice();

    if (!isAlreadyJson) {
      // 將原本「非 JSON 主列」就地轉為 JSON 第一列
      const replaced = next.map((r) =>
        r.rowKey === sourceRow.rowKey
          ? {
              ...r,
              inputType: 'json',
              jsonKey: '',
              rowKey: `${r.fieldId}::`,
              originalInputType: r.originalInputType || r.inputType
            }
          : r
      );
      commit(replaced);
      return;
    }

    // 已是 JSON 列：在其後插入一個新的 JSON 子列（共享同一個 fieldId）
    const idx = next.findIndex((r) => r.rowKey === sourceRow.rowKey);
    const newRow = {
      ...sourceRow,
      rowKey: `${sourceRow.fieldId}::__new_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      jsonKey: '',
      displayLabel: sourceRow.originalLabel || sourceRow.fieldId,
      showInList: true
    };
    next.splice(idx + 1, 0, newRow);
    commit(next);
  };

  // 刪除一列；若這是某 fieldId 的最後一個 JSON 子列，則還原為「非 JSON 主列」
  const handleRemoveRow = (sourceRow) => {
    const isJson = (sourceRow.inputType || '') === 'json' || !!sourceRow.jsonKey;
    if (!isJson) return;

    const siblingJsonRows = sortedRows.filter(
      (r) => r.fieldId === sourceRow.fieldId && ((r.inputType || '') === 'json' || !!r.jsonKey)
    );

    if (siblingJsonRows.length > 1) {
      // 還有其他 JSON 子列，直接移除這一列
      const next = sortedRows.filter((r) => r.rowKey !== sourceRow.rowKey);
      commit(next);
      return;
    }

    // 這是該 fieldId 唯一的 JSON 列 → 還原為非 JSON 主列
    const restoredType = sourceRow.originalInputType || sourceRow.fieldType || '';
    const next = sortedRows.map((r) =>
      r.rowKey === sourceRow.rowKey
        ? {
            ...r,
            inputType: restoredType,
            jsonKey: '',
            rowKey: r.fieldId
          }
        : r
    );
    commit(next);
  };

  const typeColor = (type) => {
    const colorMap = {
      text_input: 'blue',
      text_area: 'geekblue',
      date_picker: 'cyan',
      calendar_picker: 'cyan',
      time_picker: 'cyan',
      select: 'orange',
      checkbox: 'green',
      radio: 'green',
      chips_selector: 'lime',
      photo_picker: 'purple',
      document_picker: 'purple',
      opt_in: 'gold',
      input: 'blue',
      textarea: 'geekblue'
    };
    return colorMap[type] || 'default';
  };

  const columns = [
    {
      title: '#',
      key: 'order',
      width: 50,
      render: (_, __, index) => (
        <Text type="secondary" style={{ fontSize: 12 }}>{index + 1}</Text>
      )
    },
    {
      title: t('eformDesigner.fieldDisplaySetting.tableColumns.fieldType') || '類型',
      dataIndex: 'fieldType',
      key: 'fieldType',
      width: 130,
      render: (type, record) => {
        const isJson = (record.inputType || '') === 'json' || !!record.jsonKey;
        return (
          <Space size={4}>
            <Tag color={typeColor(type)}>
              {type === 'text_input' && record.inputType && record.inputType !== 'text_input' && record.inputType !== 'json'
                ? record.inputType
                : type}
            </Tag>
            {isJson && <Tag color="purple">JSON</Tag>}
          </Space>
        );
      }
    },
    ...(showScreenColumn
      ? [{
          title: 'Screen',
          dataIndex: 'screenIndex',
          key: 'screenIndex',
          width: 80,
          render: (idx) =>
            idx == null
              ? <Text type="secondary" style={{ fontSize: 12 }}>-</Text>
              : <Text type="secondary" style={{ fontSize: 12 }}>#{idx + 1}</Text>
        }]
      : []),
    {
      title: t('eformDesigner.fieldDisplaySetting.tableColumns.originalLabel') || '原始標籤',
      dataIndex: 'originalLabel',
      key: 'originalLabel',
      width: 160,
      ellipsis: true
    },
    {
      title: 'JSON Key',
      key: 'jsonKey',
      width: 180,
      render: (_, record) => {
        const isJson = (record.inputType || '') === 'json' || !!record.jsonKey;
        if (!isJson) return <Text type="secondary" style={{ fontSize: 12 }}>-</Text>;
        return (
          <Input
            size="small"
            value={record.jsonKey || ''}
            placeholder={t('eformDesigner.fieldDisplaySetting.placeholders.jsonKey') || '輸入 JSON Key'}
            onChange={(e) => {
              const nextJsonKey = e.target.value;
              const next = sortedRows.map((r) =>
                r.rowKey === record.rowKey
                  ? { ...r, jsonKey: nextJsonKey, rowKey: `${r.fieldId}::${nextJsonKey}` }
                  : r
              );
              commit(next);
            }}
          />
        );
      }
    },
    {
      title: t('eformDesigner.fieldDisplaySetting.tableColumns.displayLabel') || '顯示標籤',
      dataIndex: 'displayLabel',
      key: 'displayLabel',
      width: 200,
      render: (text, record) => (
        <Input
          size="small"
          value={text}
          onChange={(e) => handleFieldChange(record.rowKey, 'displayLabel', e.target.value)}
          placeholder={t('eformDesigner.fieldDisplaySetting.placeholders.displayLabel') || '輸入顯示標籤'}
        />
      )
    },
    {
      title: t('eformDesigner.fieldDisplaySetting.tableColumns.showInList') || '在列表中顯示',
      dataIndex: 'showInList',
      key: 'showInList',
      width: 110,
      align: 'center',
      render: (checked, record) => (
        <Switch
          checked={!!checked}
          onChange={(val) => handleFieldChange(record.rowKey, 'showInList', val)}
        />
      )
    },
    {
      title: t('eformDesigner.fieldDisplaySetting.tableColumns.fieldId') || '字段ID',
      dataIndex: 'fieldId',
      key: 'fieldId',
      width: 140,
      ellipsis: true,
      render: (text) => <Text code style={{ fontSize: 12 }}>{text}</Text>
    },
    {
      title: t('eformDesigner.fieldDisplaySetting.tableColumns.actions') || '操作',
      key: 'actions',
      width: 160,
      fixed: 'right',
      render: (_, record, index) => {
        const isJson = (record.inputType || '') === 'json' || !!record.jsonKey;
        return (
          <Space size={2}>
            <Tooltip title={t('eformDesigner.fieldDisplaySetting.actions.moveUp') || '上移'}>
              <Button
                size="small"
                type="text"
                icon={<ArrowUpOutlined />}
                disabled={index === 0}
                onClick={() => handleMove(record.rowKey, 'up')}
              />
            </Tooltip>
            <Tooltip title={t('eformDesigner.fieldDisplaySetting.actions.moveDown') || '下移'}>
              <Button
                size="small"
                type="text"
                icon={<ArrowDownOutlined />}
                disabled={index === sortedRows.length - 1}
                onClick={() => handleMove(record.rowKey, 'down')}
              />
            </Tooltip>
            {supportJson && (
              <Tooltip
                title={
                  isJson
                    ? (t('eformDesigner.fieldDisplaySetting.actions.addJsonRow') || '新增 JSON 子列')
                    : (t('eformDesigner.fieldDisplaySetting.actions.convertToJson') || '轉為 JSON 模式')
                }
              >
                <Button
                  size="small"
                  type="text"
                  icon={<PlusOutlined />}
                  onClick={() => handleAddJsonRow(record)}
                />
              </Tooltip>
            )}
            {isJson && (
              <Tooltip title={t('eformDesigner.fieldDisplaySetting.actions.removeJsonRow') || '移除 JSON 子列'}>
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemoveRow(record)}
                />
              </Tooltip>
            )}
          </Space>
        );
      }
    }
  ];

  return (
    <Table
      columns={columns}
      dataSource={sortedRows}
      rowKey="rowKey"
      pagination={false}
      size="small"
      scroll={{ y: 460, x: 'max-content' }}
      locale={{
        emptyText: t('eformDesigner.fieldDisplaySetting.messages.noFieldsFound') || '未找到表單字段'
      }}
    />
  );
};

export default FieldDisplaySettingTable;
